import { Inject, inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of, shareReplay, switchMap } from 'rxjs';
import { SimplifiedUser } from '@galaxy/crm/static';
import { ACCOUNT_GROUP_TOKEN } from '../../tokens';
import { AccountGroup } from '../account-group';
import { AssociatedUserInterface, ListUsersForBusinessResponse, UserService } from '@vendasta/business-center';

export const userFilterFn = (user: SimplifiedUser, searchTerm: string): boolean => {
  const includesFullName = user.displayName?.toLowerCase().includes(searchTerm?.toLowerCase());
  const includesFirstName = user.firstName?.toLowerCase().includes(searchTerm?.toLowerCase());
  const includesLastName = user.lastName?.toLowerCase().includes(searchTerm?.toLowerCase());
  const includesUserId = user.userId?.toLowerCase().includes(searchTerm?.toLowerCase());
  return includesFullName || includesFirstName || includesLastName || includesUserId;
};

@Injectable({ providedIn: 'root' })
export class CRMUserService {
  private userService = inject(UserService);

  private allTeamMembers$ = this.accountGroup$.pipe(
    switchMap((accountGroup) => {
      if (!accountGroup) return of(null);
      return this.userService.listUsersForBusiness(accountGroup.accountGroupId).pipe(catchError(() => of(null)));
    }),
    map((res: ListUsersForBusinessResponse | null) => {
      if (res === null || !res.associatedUsers) return [];

      const { associatedUsers } = res;
      return associatedUsers.map(
        (user) =>
          ({
            userId: user.userId,
            firstName: user.firstName,
            lastName: user.lastName,
            displayName: this.buildDisplayName(user),
          }) satisfies SimplifiedUser,
      );
    }),
    shareReplay(1),
  );

  constructor(@Inject(ACCOUNT_GROUP_TOKEN) private readonly accountGroup$: Observable<AccountGroup>) {}

  private buildDisplayName(user: AssociatedUserInterface): string {
    return `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || 'system';
  }

  searchUsers(searchTerm?: string): Observable<SimplifiedUser[]> {
    const search = searchTerm?.trim().replace(/\s+/g, ' ').split(' ');

    return this.allTeamMembers$.pipe(
      map((users) =>
        (users || []).filter((user) => {
          if (!search?.length) {
            return true;
          }

          return search.some(
            (term) => user?.displayName.toLowerCase().includes(term.toLowerCase()) || user?.userId.includes(term),
          );
        }),
      ),
    );
  }

  getMultiUsers(userIds: string[]): Observable<SimplifiedUser[]> {
    return this.allTeamMembers$.pipe(
      map((users) => {
        return userIds.map((userId) => users.find((u) => u?.userId === userId));
      }),
      map((foundUsers) => {
        return foundUsers.map((user) => {
          if (!user) {
            return {} as SimplifiedUser;
          }
          return user;
        });
      }),
    );
  }
}
